/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import Vue from "vue";
import { IWssEvent, IWssConnectedPayload } from "./wss-event.interface";
import { isWssEventPattern, matchesWssEventPattern } from "./wss-event-pattern";

/**
 * Type-safe event handler for WSS events
 */
export type WssEventHandler<T = Record<string, unknown>> = (event: IWssEvent<T>) => void;

/**
 * Handler for connection events
 */
export type WssConnectedHandler = (payload: IWssConnectedPayload) => void;

/**
 * Handler for error events
 */
export type WssErrorHandler = (error: Error) => void;

/**
 * A handler registered for an event name pattern, e.g. "*.create.comment"
 */
interface IWssPatternListener {
  handler: WssEventHandler<unknown>;

  /**
   * Remove the listener after its first call
   */
  once: boolean;
}

/**
 * Scalable event emitter for WebSocket events.
 * Uses Vue's event system for compatibility with Vue 2.
 *
 * Business logic listeners can subscribe to specific events using the event name
 * from the backend (e.g., "report.create", "message.create"), or to a pattern where
 * "*" stands for one segment of the event name (e.g., "*.create.comment" for every new comment,
 * see `matchesWssEventPattern`).
 *
 * @example
 * ```typescript
 * // Subscribe to a specific event
 * WssEventEmitter.on("report.create", (event) => {
 *   console.log("Report created:", event.data);
 * });
 *
 * // Subscribe to a pattern
 * WssEventEmitter.on("*.create.comment", (event) => {
 *   console.log("Comment created on", event.data.source);
 * });
 *
 * // Subscribe to all partner events
 * WssEventEmitter.onAny((event) => {
 *   console.log("Any event:", event);
 * });
 *
 * // Unsubscribe
 * WssEventEmitter.off("report.create", handler);
 * ```
 */
class WssEventEmitterClass {
  private readonly eventBus = new Vue();

  /**
   * Handlers registered for event name patterns, keyed by pattern.
   * Vue's event bus only knows exact names, so patterns are matched here on every emit.
   */
  private readonly patternListeners = new Map<string, IWssPatternListener[]>();

  /**
   * Event name for catching all partner events
   */
  private static readonly ALL_EVENTS = "*";

  /**
   * Event name for connection events
   */
  private static readonly CONNECTED = "wss:connected";

  /**
   * Event name for error events
   */
  private static readonly ERROR = "wss:error";

  /**
   * Event name for disconnect events
   */
  private static readonly DISCONNECTED = "wss:disconnected";

  /**
   * Subscribe to a specific event type or to a pattern.
   * The event name corresponds to the backend event name (e.g., "report.create").
   * A pattern uses "*" for one segment of the event name (e.g., "*.create.comment").
   *
   * @param eventName The event name or pattern to subscribe to
   * @param handler The handler function to call when the event is received
   */
  on<T = Record<string, unknown>>(eventName: string, handler: WssEventHandler<T>): void {
    if (this.isPattern(eventName)) {
      this.addPatternListener(eventName, handler, false);
      return;
    }

    this.eventBus.$on(eventName, handler);
  }

  /**
   * Subscribe to all partner events.
   * This is useful for logging or debugging purposes.
   *
   * @param handler The handler function to call for any event
   */
  onAny<T = Record<string, unknown>>(handler: WssEventHandler<T>): void {
    this.eventBus.$on(WssEventEmitterClass.ALL_EVENTS, handler);
  }

  /**
   * Subscribe to connection events.
   *
   * @param handler The handler function to call when connected
   */
  onConnected(handler: WssConnectedHandler): void {
    this.eventBus.$on(WssEventEmitterClass.CONNECTED, handler);
  }

  /**
   * Subscribe to error events.
   *
   * @param handler The handler function to call on error
   */
  onError(handler: WssErrorHandler): void {
    this.eventBus.$on(WssEventEmitterClass.ERROR, handler);
  }

  /**
   * Subscribe to disconnect events.
   *
   * @param handler The handler function to call when disconnected
   */
  onDisconnected(handler: (reason: string) => void): void {
    this.eventBus.$on(WssEventEmitterClass.DISCONNECTED, handler);
  }

  /**
   * Unsubscribe from a specific event type or pattern.
   *
   * @param eventName The event name or pattern to unsubscribe from
   * @param handler The handler function to remove (optional, removes all if not provided)
   */
  off<T = Record<string, unknown>>(eventName: string, handler?: WssEventHandler<T>): void {
    if (this.isPattern(eventName)) {
      this.removePatternListener(eventName, handler);
      return;
    }

    this.eventBus.$off(eventName, handler);
  }

  /**
   * Unsubscribe from all events subscription.
   *
   * @param handler The handler function to remove
   */
  offAny<T = Record<string, unknown>>(handler?: WssEventHandler<T>): void {
    this.eventBus.$off(WssEventEmitterClass.ALL_EVENTS, handler);
  }

  /**
   * Unsubscribe from connection events.
   *
   * @param handler The handler function to remove
   */
  offConnected(handler?: WssConnectedHandler): void {
    this.eventBus.$off(WssEventEmitterClass.CONNECTED, handler);
  }

  /**
   * Unsubscribe from error events.
   *
   * @param handler The handler function to remove
   */
  offError(handler?: WssErrorHandler): void {
    this.eventBus.$off(WssEventEmitterClass.ERROR, handler);
  }

  /**
   * Unsubscribe from disconnect events.
   *
   * @param handler The handler function to remove
   */
  offDisconnected(handler?: (reason: string) => void): void {
    this.eventBus.$off(WssEventEmitterClass.DISCONNECTED, handler);
  }

  /**
   * Subscribe to an event or pattern once. The handler will be removed after the first call.
   *
   * @param eventName The event name or pattern to subscribe to
   * @param handler The handler function to call once
   */
  once<T = Record<string, unknown>>(eventName: string, handler: WssEventHandler<T>): void {
    if (this.isPattern(eventName)) {
      this.addPatternListener(eventName, handler, true);
      return;
    }

    this.eventBus.$once(eventName, handler);
  }

  /**
   * Emit a partner event. Called internally by the WebSocket service.
   *
   * @param event The WSS event received from the backend
   */
  emit<T = Record<string, unknown>>(event: IWssEvent<T>): void {
    // Emit to specific event listeners
    this.eventBus.$emit(event.event, event);

    // Emit to "all events" listeners
    this.eventBus.$emit(WssEventEmitterClass.ALL_EVENTS, event);

    // Emit to pattern listeners
    this.emitToPatternListeners(event);
  }

  /**
   * Emit a connected event. Called internally by the WebSocket service.
   *
   * @param payload The connected payload
   */
  emitConnected(payload: IWssConnectedPayload): void {
    this.eventBus.$emit(WssEventEmitterClass.CONNECTED, payload);
  }

  /**
   * Emit an error event. Called internally by the WebSocket service.
   *
   * @param error The error
   */
  emitError(error: Error): void {
    this.eventBus.$emit(WssEventEmitterClass.ERROR, error);
  }

  /**
   * Emit a disconnected event. Called internally by the WebSocket service.
   *
   * @param reason The disconnect reason
   */
  emitDisconnected(reason: string): void {
    this.eventBus.$emit(WssEventEmitterClass.DISCONNECTED, reason);
  }

  /**
   * Remove all event listeners. Useful for cleanup.
   */
  removeAllListeners(): void {
    this.eventBus.$off();
    this.patternListeners.clear();
  }

  /**
   * Whether the event name is handled by pattern matching.
   * The bare "*" keeps its meaning as the "all events" channel of the event bus.
   */
  private isPattern(eventName: string): boolean {
    return eventName !== WssEventEmitterClass.ALL_EVENTS && isWssEventPattern(eventName);
  }

  private addPatternListener<T>(pattern: string, handler: WssEventHandler<T>, once: boolean): void {
    const listeners = this.patternListeners.get(pattern) ?? [];
    listeners.push({ handler: handler as WssEventHandler<unknown>, once });
    this.patternListeners.set(pattern, listeners);
  }

  /**
   * Removes the most recently added listener with the given handler, like `$off` on the event bus does,
   * or every listener of the pattern when no handler is given.
   */
  private removePatternListener<T>(pattern: string, handler?: WssEventHandler<T>): void {
    const listeners = this.patternListeners.get(pattern);
    if (!listeners) {
      return;
    }

    if (!handler) {
      this.patternListeners.delete(pattern);
      return;
    }

    for (let index = listeners.length - 1; index >= 0; index--) {
      if (listeners[index].handler === handler) {
        listeners.splice(index, 1);
        break;
      }
    }

    if (!listeners.length) {
      this.patternListeners.delete(pattern);
    }
  }

  private emitToPatternListeners<T>(event: IWssEvent<T>): void {
    this.patternListeners.forEach((listeners, pattern) => {
      if (!matchesWssEventPattern(pattern, event.event)) {
        return;
      }

      // Iterate over a copy: "once" listeners remove themselves while the list is being walked
      for (const listener of [...listeners]) {
        if (listener.once) {
          this.removePatternListener(pattern, listener.handler);
        }

        // Keep the other listeners running when one of them throws, like the event bus does
        try {
          listener.handler(event as IWssEvent<unknown>);
        } catch (error) {
          const logError = Vue.prototype.$log?.error ?? console.error;
          logError(`[WssEventEmitter] Error in handler for pattern "${pattern}"`, error);
        }
      }
    });
  }
}

/**
 * Singleton instance of the WSS event emitter.
 * Use this to subscribe to WebSocket events throughout the application.
 */
export const WssEventEmitter = new WssEventEmitterClass();
